import { LitElement, css, html, nothing } from "lit";
import { CHAT_COLUMN_MAX_WIDTH } from "../chat-column-metrics.js";
import { customElement, property } from "lit/decorators.js";
import type { ChatLabels } from "../types.js";

/**
 * afas-alert (checked against 5.0.24) keeps its action buttons on the text's row at every width, so on
 * a phone the reconnect button would squeeze the text into a sliver. Below 560px the
 * actions drop onto their own full-width row instead. The icon, which the alert pins
 * to the first text line, is centred on the text instead, as in the composer's
 * notices. Those parts live in the alert's shadow root with no part exposed, so the
 * rules are injected there, the way the composer shapes its notices (see
 * applyNoticeOverrides in jonas-chat-composer.ts).
 */
const ALERT_OVERRIDES = `
  .alert afas-icon, .alert afas-icon.has-actions { align-self: center; margin-top: 0; }
  @media (max-width: 560px) {
    .alert .content.has-actions { flex-wrap: wrap; }
    .alert .action-buttons { flex: 1 0 100%; }
  }
`;

/** Alert shadow roots that already carry the sheet above, so it is injected once. */
const alertOverrideRoots = new WeakSet<ShadowRoot>();

@customElement("help-jonas-availability-banner")
export class HelpJonasAvailabilityBanner extends LitElement {
  static override styles = css`
    /*
     * The banner sits in the composer's column: the composer insets itself by 24px
     * and caps its content at CHAT_COLUMN_MAX_WIDTH, so matching that puts their edges on the same
     * lines (see .chat-notice in jonas-chat-thread.ts for the same rule).
     */
    :host {
      display: block;
      padding: 12px 24px 8px;
    }
    .banner {
      display: block;
      box-sizing: border-box;
      max-width: ${CHAT_COLUMN_MAX_WIDTH}px;
      margin: 0 auto;
    }
    /*
     * The recovery state confirms the AI Platform is back and reads as a success
     * follow-up to the warning it replaces. afas-alert has no positive type, so its
     * info tone is pointed at the positive tokens for this one alert.
     */
    .banner--restored {
      --color-primary-50: var(--color-positive-50);
      --color-primary: var(--color-positive-600);
    }
  `;

  @property({ attribute: false })
  accessor labels: ChatLabels | null = null;

  // Switches the banner to the positive (green) "available again" state shown
  // while the recovered platform re-answers the original question. It drops the
  // reconnect CTA (a retry is already in flight) and the warning iconography.
  @property({ type: Boolean })
  accessor restored = false;

  // True while a reconnect attempt is in flight (any trigger: this button, the idle
  // re-probe timer, or a fallback search). It disables the reconnect button and swaps
  // its label to the busy "Bezig verbinding te herstellen" spinner state so the user
  // gets one clear "reconnecting" signal and cannot fire a second competing attempt.
  @property({ type: Boolean })
  accessor reconnecting = false;

  protected override render() {
    return this.restored ? this.renderRestored() : this.renderUnavailable();
  }

  protected override updated(): void {
    this.applyAlertOverrides();
  }

  private applyAlertOverrides(): void {
    const root = this.renderRoot.querySelector("afas-alert")?.shadowRoot;
    if (!root || alertOverrideRoots.has(root)) return;
    if (typeof CSSStyleSheet === "undefined") return;
    if (!("adoptedStyleSheets" in root)) return;
    try {
      const sheet = new CSSStyleSheet();
      sheet.replaceSync(ALERT_OVERRIDES);
      root.adoptedStyleSheets = [...root.adoptedStyleSheets, sheet];
      alertOverrideRoots.add(root);
    } catch {
      // Best-effort: the button then shares the text's row on phones as well.
    }
  }

  private renderUnavailable() {
    const title = this.getLabel(
      "availabilityBannerTitle",
      "Jonas (AI) niet beschikbaar",
    );
    const description = this.getLabel(
      "availabilityBannerDescription",
      "Je kunt blijven zoeken in onze bronnen.",
    );
    const reconnectLabel = this.getLabel(
      "availabilityReconnect",
      "Opnieuw verbinden met Jonas (AI)",
    );
    const reconnectingLabel = this.getLabel(
      "availabilityReconnecting",
      "Bezig verbinding te herstellen",
    );
    return html`<afas-alert
      class="banner"
      type="warning"
      header=${title}
      role="status"
      aria-live="polite"
    >
      <span class="banner__description">${description}</span>
      <afas-button
        slot="actions"
        variant="default"
        ?disabled=${this.reconnecting}
        .label=${this.reconnecting ? reconnectingLabel : reconnectLabel}
        @click=${this.handleReconnect}
      >
        ${this.reconnecting
          ? html`<afas-loading-spinner
              slot="icon"
              size="small"
            ></afas-loading-spinner>`
          : nothing}
      </afas-button>
    </afas-alert>`;
  }

  private renderRestored() {
    const title = this.getLabel(
      "availabilityRestoredTitle",
      "Jonas (AI) weer beschikbaar",
    );
    return html`<afas-alert
      class="banner banner--restored"
      type="info"
      header=${title}
      role="status"
      aria-live="polite"
    ></afas-alert>`;
  }

  private handleReconnect(): void {
    this.dispatchEvent(
      new CustomEvent("jonas-ai-reconnect", { bubbles: true, composed: true }),
    );
  }

  private getLabel(key: string, fallback: string): string {
    return this.labels?.[key] || fallback;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "help-jonas-availability-banner": HelpJonasAvailabilityBanner;
  }
}
